import { useId, useRef, useCallback } from 'react';
import type { RadioGroupProps, RadioSize } from './types';

// ─── Size class map ───────────────────────────────────────────────────────────

const SIZE_CLASSES: Record<RadioSize, { circle: string; dot: string }> = {
  // 16px circle
  sm: {
    circle: 'h-4 w-4',
    dot: 'h-1.5 w-1.5',
  },
  // 20px circle
  md: {
    circle: 'h-5 w-5',
    dot: 'h-2 w-2',
  },
};

// ─── RadioGroup ───────────────────────────────────────────────────────────────

export function RadioGroup({
  options,
  value,
  onChange,
  label,
  variant = 'default',
  disabled = false,
  error,
  size = 'md',
  className,
}: RadioGroupProps) {
  const generatedId = useId();
  const groupId = generatedId;
  const errorId = error ? `${groupId}-error` : undefined;

  const s = SIZE_CLASSES[size];

  // Track refs for each enabled option to support arrow-key navigation
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const enabledIndices = options.reduce<number[]>((acc, opt, i) => {
        if (!disabled && !opt.disabled) acc.push(i);
        return acc;
      }, []);
      if (enabledIndices.length === 0) return;

      const currentIndex = options.findIndex((opt) => opt.value === value);
      const currentEnabledPos = enabledIndices.indexOf(currentIndex);

      let nextEnabledPos: number | null = null;
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        nextEnabledPos = (currentEnabledPos + 1) % enabledIndices.length;
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        nextEnabledPos =
          (currentEnabledPos - 1 + enabledIndices.length) % enabledIndices.length;
      }

      if (nextEnabledPos !== null) {
        e.preventDefault();
        const nextIndex = enabledIndices[nextEnabledPos];
        onChange(options[nextIndex].value);
        inputRefs.current[nextIndex]?.focus();
      }
    },
    [options, value, disabled, onChange],
  );

  // ─── Container layout ──────────────────────────────────────────────────────

  const containerClasses = [
    variant === 'default' && 'flex flex-col gap-2',
    variant === 'horizontal' && 'flex flex-row flex-wrap gap-4',
    variant === 'card' && 'flex flex-col gap-2 sm:flex-row sm:flex-wrap',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={className}>
      {label && (
        <p className="mb-2 text-sm font-medium text-neutral-700 dark:text-neutral-300">{label}</p>
      )}

      <div
        role="radiogroup"
        aria-label={label}
        aria-describedby={errorId}
        onKeyDown={handleKeyDown}
        className={containerClasses}
      >
        {options.map((option, index) => {
          const isSelected = option.value === value;
          const isDisabled = disabled || option.disabled === true;
          const optionId = `${groupId}-${index}`;

          // ─── Per-option circle classes ───────────────────────────────────

          const circleClasses = [
            'shrink-0 rounded-full border-2 flex items-center justify-center transition-colors duration-150',
            s.circle,
            isSelected
              ? 'border-brand-500'
              : error
                ? 'border-danger-500'
                : 'border-neutral-300 dark:border-neutral-600',
            isDisabled
              ? 'opacity-50 cursor-not-allowed bg-neutral-200 dark:bg-neutral-700 border-neutral-400 dark:border-neutral-500'
              : !isSelected && !error
                ? 'bg-white dark:bg-neutral-900 hover:border-brand-500'
                : 'bg-white dark:bg-neutral-900',
          ]
            .filter(Boolean)
            .join(' ');

          const labelClasses = [
            'text-sm font-medium leading-5 select-none',
            isDisabled
              ? 'text-neutral-400 dark:text-neutral-500 cursor-not-allowed'
              : 'text-neutral-700 dark:text-neutral-300 cursor-pointer',
          ].join(' ');

          // ─── Card wrapper classes ────────────────────────────────────────

          const cardClasses =
            variant === 'card'
              ? [
                  'flex items-start gap-3 rounded-lg border-2 p-3 transition-colors duration-150',
                  isSelected
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-950'
                    : error
                      ? 'border-danger-500'
                      : 'border-neutral-200 dark:border-neutral-700',
                  isDisabled
                    ? 'opacity-50 cursor-not-allowed'
                    : 'cursor-pointer hover:border-brand-400',
                ]
                  .filter(Boolean)
                  .join(' ')
              : undefined;

          const optionContent = (
            <>
              {/* 44×44 touch target wrapper on mobile */}
              <span className="relative flex items-center justify-center p-[2px] sm:p-0 -m-[2px] sm:m-0 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0">
                <input
                  ref={(el) => {
                    inputRefs.current[index] = el;
                  }}
                  type="radio"
                  id={optionId}
                  name={groupId}
                  value={option.value}
                  checked={isSelected}
                  disabled={isDisabled}
                  aria-checked={isSelected}
                  onChange={() => {
                    if (!isDisabled) onChange(option.value);
                  }}
                  className="sr-only peer"
                />
                {/* Visual circle */}
                <label htmlFor={optionId} aria-hidden="true" className={circleClasses}>
                  {isSelected && (
                    <span className={`rounded-full bg-brand-500 ${s.dot}`} aria-hidden="true" />
                  )}
                </label>
                {/* Focus ring overlay */}
                <span className="absolute inset-0 rounded-full peer-focus-visible:ring-2 peer-focus-visible:ring-brand-500 peer-focus-visible:ring-offset-2 pointer-events-none" />
              </span>

              {/* Label + optional description */}
              <label htmlFor={optionId} className={labelClasses}>
                {option.label}
                {option.description && (
                  <span className="block text-xs font-normal text-neutral-500 dark:text-neutral-400 mt-0.5">
                    {option.description}
                  </span>
                )}
              </label>
            </>
          );

          return variant === 'card' ? (
            <div key={option.value} className={cardClasses}>
              {optionContent}
            </div>
          ) : (
            <div key={option.value} className="flex items-start gap-2">
              {optionContent}
            </div>
          );
        })}
      </div>

      {error && (
        <p id={errorId} className="mt-1 text-xs text-danger-500">
          {error}
        </p>
      )}
    </div>
  );
}
